import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import type { AdminApi } from "@/api";
import { Badge, Banner, Button, Card } from "@/components/ui";
import { cn } from "@/lib/utils";
import { DomainChip } from "./ReferenceBadges";
import { ReferenceFilterBar, type ReferenceFilters, useDebounced, useReferenceList } from "./ReferenceReviewTab";
import { kstShort } from "./referenceCases";

type Scope = "latest_passed" | "versions";

/**
 * Test Cases → Reference Answer → 통과 모음. The answers people passed: by
 * default each TC's most recently passed version, or every passed version.
 * "채점 기준" says when the TC is scored against another version.
 */
export function ReferencePassedTab({
  api,
  reloadKey,
  onOpen,
}: {
  api: AdminApi;
  reloadKey: number;
  /** Opens the TC on the 검토 tab. */
  onOpen: (tc: string, snap: string) => void;
}) {
  const [scope, setScope] = useState<Scope>("latest_passed");
  const [filters, setFilters] = useState<ReferenceFilters>({ q: "", domain: "", env: "" });
  const q = useDebounced(filters.q.trim());
  const query = useMemo(
    () => ({
      scope,
      review_status: scope === "versions" ? ("passed" as const) : undefined,
      source_environment: filters.env || undefined,
      domain: filters.domain || undefined,
      q: q || undefined,
    }),
    [scope, filters.env, filters.domain, q],
  );
  const { list, error, more, loadingMore } = useReferenceList(api, query, `${JSON.stringify(query)}|${reloadKey}`);
  const items = list?.items ?? [];

  return (
    <div className="grid gap-4.5">
      {error && <Banner tone="error">통과한 Reference를 읽지 못했습니다: {error}</Banner>}
      <Card className="min-w-0">
        <ReferenceFilterBar filters={filters} onChange={setFilters}>
          <div className="flex gap-1" role="group" aria-label="보기">
            {(
              [
                ["latest_passed", "TC마다 최신 통과 버전"],
                ["versions", "통과한 버전 전부"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={scope === value}
                onClick={() => setScope(value)}
                className={cn(
                  "rounded-full border border-border bg-card px-3 py-1 text-[12.5px]",
                  scope === value && "border-primary bg-accent text-accent-foreground",
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </ReferenceFilterBar>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="border-b border-border text-left text-[11.5px] text-muted-foreground">
                <th className="px-2.5 py-2 font-medium">질문과 통과한 답변</th>
                <th className="px-2.5 py-2 font-medium whitespace-nowrap">버전</th>
                <th className="px-2.5 py-2 font-medium whitespace-nowrap max-md:hidden">통과</th>
                <th className="px-2.5 py-2 font-medium whitespace-nowrap">채점 기준</th>
              </tr>
            </thead>
            <tbody>
              {!list && !error && (
                <tr>
                  <td colSpan={4} className="px-3 py-5 text-muted-foreground">
                    <Loader2 className="mr-1.5 inline size-4 animate-spin" />
                    목록을 읽는 중…
                  </td>
                </tr>
              )}
              {list && items.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-5 text-muted-foreground">
                    통과한 Reference가 없습니다.
                  </td>
                </tr>
              )}
              {items.map((item) => (
                <tr key={item.snapshot_id} className="border-b border-border align-top last:border-0">
                  <td className="max-w-0 px-2.5 py-2 md:w-[64%]">
                    <button
                      type="button"
                      className="block w-full truncate text-left font-medium hover:underline"
                      onClick={() => onOpen(item.test_case_id, item.snapshot_id)}
                    >
                      {item.test_case.title || item.test_case.trigger_preview || item.test_case_id.slice(0, 8)}
                    </button>
                    <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                      <DomainChip domain={item.test_case.domain} />
                      {item.test_case.source_environment}
                    </div>
                    <details className="mt-1 text-xs">
                      <summary className="cursor-pointer text-muted-foreground">답변 보기</summary>
                      <div className="mt-1 max-h-72 overflow-y-auto rounded-md bg-muted px-2.5 py-2 leading-relaxed whitespace-pre-wrap">
                        {item.answer_body}
                      </div>
                    </details>
                  </td>
                  <td className="px-2.5 py-2 whitespace-nowrap tabular-nums">
                    v{item.version} / {item.version_count}
                  </td>
                  <td className="px-2.5 py-2 text-[12px] whitespace-nowrap max-md:hidden">
                    {item.review?.reviewer_email}
                    <div className="text-[11px] text-muted-foreground tabular-nums">{kstShort(item.review?.reviewed_at)}</div>
                  </td>
                  <td className="px-2.5 py-2 whitespace-nowrap">
                    {item.active ? (
                      <Badge size="compact" className="border-success-line bg-success-surface text-success">
                        이 버전
                      </Badge>
                    ) : (
                      <Badge size="compact" className="border-accent-line bg-accent text-accent-foreground">
                        {item.active_version != null ? `v${item.active_version} 사용 중` : "없음"}
                      </Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {list?.next_cursor && (
          <div className="border-t border-border px-3 py-2.5">
            <Button variant="outline" size="sm" disabled={loadingMore} onClick={() => void more()}>
              {loadingMore && <Loader2 className="animate-spin" />}더 보기
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
